<template>
  <loading-content :loading="loading">
    <v-row class="fill-height" :class="plate ? ['cal-plate', 'cal-look-' + look, { 'cal-page': pageScroll, 'cal-phone': phone, 'cal-listmode': phone && phoneView === 'list' }] : null">
      <v-col>
        <!-- The new look (Süsteem -> Seaded -> Kalender uues kujunduses):
             what is shown and when above a wide title, the views as
             one row of buttons and the three filters as chips anyone can see
             are on. The same choices as the toolbar below, nothing more.
             One row — title, chips in the middle, buttons — where it fits;
             narrower, the chips go to a row of their own (see .cal-head). -->
        <!-- Kalender telefonis (Seaded): the date on one small line between
             ‹ and ›, the day buttons below say which day it is; Päev or
             Nimekiri and the filters, each its own button. -->
        <div v-if="phone" class="cal-phead">
          <div class="cal-phead__row">
            <button type="button" class="cal-nav" aria-label="Eelmine" @click="prev"><v-icon>mdi-chevron-left</v-icon></button>
            <span class="cal-cap cal-mid cal-phead__cap"><template v-for="(part, i) in phoneCaption" :key="i"><template v-if="i"> · </template><span>{{ part }}</span></template></span>
            <button type="button" class="cal-nav" aria-label="Järgmine" @click="next"><v-icon>mdi-chevron-right</v-icon></button>
            <button v-if="!onToday" type="button" class="cal-today" @click="setToday">Täna</button>
          </div>
          <!-- Päev / Nimekiri and the three filters in one row, each filter its own button. -->
          <div class="cal-phead__row cal-phead__tools">
            <div class="cal-seg" role="group" aria-label="Vaade">
              <button type="button" :aria-pressed="String(phoneView === 'day')" @click="phoneView = 'day'">Päev</button>
              <button type="button" :aria-pressed="String(phoneView === 'list')" @click="phoneView = 'list'">Nimekiri</button>
            </div>
            <div class="cal-ftgl" role="group" aria-label="Filtrid">
              <button
                v-for="setting in settingOptions"
                :key="setting.value"
                type="button"
                class="cal-ftg"
                :title="setting.label"
                :aria-pressed="String(settings.includes(setting.value))"
                @click="toggleSetting(setting.value)"
              >{{ setting.short }}</button>
            </div>
          </div>
        </div>
        <div v-else-if="plate" class="cal-head">
          <div class="cal-head__row">
            <div class="cal-head__text">
              <span class="cal-cap cal-mid">{{ caption }}</span>
              <h1 class="cal-title cal-wide">{{ title }}</h1>
            </div>
            <div class="cal-head__chips">
              <button
                v-for="setting in settingOptions"
                :key="setting.value"
                type="button"
                class="cal-chip"
                :aria-pressed="String(settings.includes(setting.value))"
                @click="toggleSetting(setting.value)"
              >
                <v-icon v-if="settings.includes(setting.value)" size="16">mdi-check</v-icon>
                {{ setting.label }}
              </button>
            </div>
            <!-- One group, so on a phone it wraps whole rather than splitting ‹ from ›. -->
            <div class="cal-head__ctl">
            <button type="button" class="cal-nav" aria-label="Eelmine" @click="prev"><v-icon>mdi-chevron-left</v-icon></button>
            <button type="button" class="cal-nav" aria-label="Järgmine" @click="next"><v-icon>mdi-chevron-right</v-icon></button>
            <button type="button" class="cal-today" @click="setToday">Täna</button>
            <div class="cal-seg" role="group" aria-label="Vaade">
              <button
                v-for="view in viewTypes"
                :key="view.key"
                type="button"
                :aria-pressed="String(type === view.key)"
                @click="type = view.key"
              >{{ view.label }}</button>
            </div>
            </div>
          </div>
        </div>
        <v-sheet v-else>
          <v-toolbar :elevation="0" color="blue" theme="dark">
            <v-toolbar-title>{{title}}</v-toolbar-title>
            <v-btn icon @click="prev">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon @click="next">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn variant="outlined" @click="setToday" class="d-none d-sm-inline-flex mr-2">
              Täna
            </v-btn>
            <!-- Vuetify 3 has no v-list-item-group; the item that is chosen
                 says so itself (`active`), and a click is what chooses it. -->
            <v-menu location="bottom end">
              <template #activator="{ props }">
                <v-btn icon v-bind="props">
                  <v-icon>{{viewTypes.find(t => t.key === type).icon}}</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  v-for="view in viewTypes"
                  :key="view.key"
                  :active="type === view.key"
                  color="primary"
                  @click="type = view.key"
                >
                  <template #prepend>
                    <v-icon>{{view.icon}}</v-icon>
                  </template>
                  <v-list-item-title>{{view.label}}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-menu location="bottom end" :close-on-content-click="false">
              <template #activator="{ props }">
                <v-btn icon v-bind="props">
                  <v-icon>mdi-dots-vertical</v-icon>
                </v-btn>
              </template>
              <v-list density="compact">
                <v-list-item
                  v-for="setting in settingOptions"
                  :key="setting.value"
                  :active="settings.includes(setting.value)"
                  color="primary"
                  @click="toggleSetting(setting.value)"
                >
                  <template #prepend>
                    <v-checkbox-btn :model-value="settings.includes(setting.value)" color="primary" density="compact" />
                  </template>
                  <v-list-item-title>{{setting.label}}</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </v-toolbar>
        </v-sheet>
        <!-- On a phone, one day at a time: the week's days as buttons. While a
             work order is held, letting go over a day moves it there. -->
        <div v-if="dayStrip" class="cal-days" role="group" aria-label="Päev">
          <button
            v-for="day in stripDays"
            :key="day.date"
            type="button"
            class="cal-day"
            :class="{ 'cal-day--today': day.today, 'cal-day--drop': touch && touch.dropDate === day.date }"
            :data-date="day.date"
            :aria-pressed="String(type === 'day' && period.startDate === day.date)"
            @click="viewDay(null, { date: day.date })"
          >{{ day.label }}<i v-if="phone" class="cal-day__n">{{ dayCounts[day.date] || '' }}</i></button>
        </div>
        <!-- Who is where today, as chips (the grid's own head is hidden on a phone). -->
        <div v-if="phone && dayPlanner.length" class="cal-pchips">
          <span v-for="plan in dayPlanner" :key="plan.id" class="cal-pchip" :title="plan.user.fullName">
            <span class="cal-ini" :style="{ background: plan.user.color }">{{ initials(plan.user) }}</span>{{ plan.workday.name }}
          </span>
        </div>
        <!-- Nimekiri: the day's work orders in time order, the address whole. -->
        <div v-if="phone && phoneView === 'list'" class="cal-list">
          <button
            v-for="ev in dayOrders"
            :key="ev.data.id"
            type="button"
            class="cal-row"
            :class="{ 'cal-row--now': isNow(ev) }"
            @click="openCard(ev)"
          >
            <span class="cal-row__t"><b>{{ hm(ev.start) }}</b><small>{{ hm(ev.end) }}</small></span>
            <span class="cal-row__b" :style="{ borderLeftColor: userColor(ev.data) }">
              <span class="cal-ini" :style="{ background: userColor(ev.data) }">{{ initials(ev.data.user) }}</span>
              <span v-if="ev.data.helper" class="cal-ini cal-ini--second" :style="{ background: ev.data.helper.color || '#1976D2' }">{{ initials(ev.data.helper) }}</span>
              <span class="cal-row__txt"><b>{{ fullAddress(ev.data) }}</b><small>{{ rowSub(ev.data) }}</small></span>
            </span>
          </button>
          <p v-if="!dayOrders.length" class="cal-list__empty">Sel päeval töökäske pole.</p>
        </div>
        <!-- The new look: the sheet reaches the bottom of the window and the
             hours scroll inside it under the day row, 08:00–18:00 filling it
             (see fitHours). -->
        <v-sheet ref="calSheet" :class="{ 'cal-fit': fitHours }" :style="fitHours && calHeight ? { height: calHeight + 'px' } : null">
          <!-- Vuetify 3's calendar is Vuetify 2's, ported, with two changes that
               matter here: the week-numbering day is first-day-of-year, and every
               handler is called (nativeEvent, data) where it used to be
               (data, nativeEvent). -->
          <v-calendar
            ref="calendar"
            v-model="focus"
            color="primary"
            :start="calendarStart"
            :events="filteredEvents"
            :event-color="getEventColor"
            :type="calendarType"
            :first-interval="16"
            :interval-count="intervalCount"
            :interval-minutes="30"
            :weekday-format="formatWeekday"
            locale="et"
            :first-day-of-year="1"
            :interval-height="intervalHeight"
            :interval-format="formatInterval"
            :weekdays="weekdays"
            :event-overlap-mode="fitHours ? laneMode : 'stack'"
            @click:event="showEvent"
            @click:more="viewDay"
            @click:date="viewDay"
            @change="changed"
            @mousedown:event="startDrag"
            @mouseleave.stop="cancelDrag"
            @mousedown:time="startTime"
            @mousemove:time="mouseMove"
            @mouseup:time="endDrag"
          >
            <!-- Which day a column is, for a work order carried there by finger. -->
            <template #day-body="{ date }">
              <span class="cal-day-mark" :data-date="date" hidden />
            </template>
            <template #event="{ event, timed, eventParsed }">
              <v-tooltip
                location="left"
                :disabled="!!touch"
                :content-class="tooltipClass(event)"
                :content-props="{ style: tooltipStyle(event) }"
              >
                <template #activator="{ props }">
                  <!-- The new look: the technician's colour as a tint and an edge,
                       initials first, the same lines as ever. -->
                  <div
                    v-if="plate"
                    v-bind="props"
                    class="event cal-ev v-event-draggable"
                    :class="{
                      'cal-ev--plan': event.data && event.data.type == 1,
                      'cal-ev--picked': timed && event.data && pickedId === event.data.id,
                      'cal-ev--lifted': timed && event.data && touch && touch.kind && touch.id === event.data.id
                    }"
                    :style="plateEventStyle(event)"
                    :data-ev-id="timed && event.data ? event.data.id : null"
                  >
                    <template v-if="event.data && event.data.type == 1">
                      <span class="cal-ini" :style="{ background: event.data.user.color }">{{ initials(event.data.user) }}</span>
                      <span class="cal-plan__name">{{ event.data.workday.name }}</span>
                    </template>
                    <!-- A month is a page of days: one line per order there. -->
                    <div v-else-if="type === 'month'" class="event-line cal-ev__month">
                      <b>{{ eventParsed.start.time }}</b>
                      {{ event.data && event.data.user ? initials(event.data.user) : '' }}<template v-if="event.data && event.data.helper">+{{ initials(event.data.helper) }}</template>
                      <template v-if="event.data"> · {{ shortStreet(event) }}</template>
                    </div>
                    <template v-else>
                      <div class="event-line cal-ev__time" :style="plateTimeStyle(event)">
                        <span v-if="event.data && event.data.user" class="cal-ini" :class="{ 'cal-ini--on': lookDef.fullTime }" :style="initialsStyle(lookDef, event.data.user.color)">{{ initials(event.data.user) }}</span>
                        <span v-if="event.data && event.data.helper" class="cal-ini cal-ini--second" :class="{ 'cal-ini--on': lookDef.fullTime }" :style="initialsStyle(lookDef, event.data.helper.color || '#1976D2')">{{ initials(event.data.helper) }}</span>
                        {{ eventParsed.start.time }}–{{ eventParsed.end.time }}
                        <span v-if="event.data && event.data.google.isSynced" class="cal-g">G</span>
                      </div>
                      <span v-html="plateLines(event)" />
                      <!-- A picked work order's end, dragged by finger. -->
                      <span v-if="timed && event.data && pickedId === event.data.id" class="cal-grip" data-grip aria-label="Muuda lõppu"><i /></span>
                    </template>
                  </div>
                  <div v-else v-bind="props" class="event v-event-draggable pa-1">
                    <span v-html="body(event, eventParsed, false)" />
                    <v-icon v-if="event.data && event.data.google.isSynced" class="synced" color="white">mdi-google</v-icon>
                  </div>
                  <div
                    v-if="timed"
                    class="v-event-drag-bottom"
                    @mousedown.stop="extendBottom(event)"
                  ></div>
                </template>
                <div v-html="body(event, eventParsed, true)" />
              </v-tooltip>
            </template>
          </v-calendar>
          <button v-if="fitHours && eveningHidden && !phone" type="button" class="cal-evening" @click="toEvening">Õhtu 18:00–22:30 ↓</button>
          <!-- A phone: an empty evening is one line, not four and a half hatched hours. -->
          <button v-if="phone && !showEvening" type="button" class="cal-eve" @click="eveningOpen = true">Õhtu 18:00–22:30 · tühi</button>
          <!-- After a work order is moved or stretched: what happened, and the way back. -->
          <div v-if="undo" class="cal-undo" role="status">
            <span>{{ undo.text }}</span>
            <button type="button" @click="undoMove">Võta tagasi</button>
          </div>
          <!-- Wider in the hooldusplaat look: its labels sit above the boxes and
               the right column carries the address's work orders. -->
          <!-- The whole screen on a phone: inside a dialog's margins the fields
               were some 250px wide. -->
          <v-dialog v-model="selectedOpen" persistent :fullscreen="$vuetify.display.xs" :max-width="features && features.eventPlate ? '880px' : '800px'">
            <event
              v-bind="eventView"
              @close="selectedOpen = false"
              @created="onNewEvent"
              @updated="onNewEvent"
              @deleted="onDeleteEvent"
            />
          </v-dialog>
          <!-- A phone: a tap shows the work order to read and act on; the form
               is one more tap away. -->
          <v-bottom-sheet v-model="cardOpen">
            <div v-if="card" class="cal-sheet">
              <div class="cal-sheet__head">
                <span class="cal-sheet__when">{{ cardWhen }}</span>
                <b class="cal-sheet__adr">{{ fullAddress(card.data) }}</b>
                <span class="cal-sheet__pill">{{ calendarCategoryById(card.data.category).text }}</span>
              </div>
              <dl class="cal-kv">
                <div v-if="card.data.client"><dt>Klient</dt><dd>{{ card.data.client.fullName }}</dd></div>
                <div v-if="card.data.client && card.data.client.phone"><dt>Telefon</dt><dd>{{ card.data.client.phone }}</dd></div>
                <div v-if="card.data.user && card.data.user.fullName"><dt>Tehnik</dt><dd><span class="cal-ini" :style="{ background: userColor(card.data) }">{{ initials(card.data.user) }}</span> {{ card.data.user.fullName }}</dd></div>
                <div v-if="card.data.helper && card.data.helper.fullName"><dt>Teine tehnik</dt><dd><span class="cal-ini" :style="{ background: card.data.helper.color || '#1976D2' }">{{ initials(card.data.helper) }}</span> {{ card.data.helper.fullName }}</dd></div>
                <div v-if="devicesOf(card.data)"><dt>Seade</dt><dd>{{ devicesOf(card.data) }}</dd></div>
                <div v-if="card.data.comments" class="cal-kv__wide"><dt>Töökäsk</dt><dd>{{ card.data.comments }}</dd></div>
              </dl>
              <div class="cal-sheet__acts">
                <a v-if="telOf(card.data)" :href="'tel:' + telOf(card.data)" class="cal-act"><v-icon size="18">mdi-phone</v-icon>Helista</a>
                <a v-if="card.data.object && card.data.object.fullAddress" :href="mapsUrl(card.data)" target="_blank" rel="noopener" class="cal-act"><v-icon size="18">mdi-map-marker-outline</v-icon>Kaart</a>
                <a v-if="canAkt(card.data)" :href="aktUrl(card.data)" target="_blank" rel="noopener" class="cal-act cal-act--go"><v-icon size="18">mdi-file-document-edit-outline</v-icon>Tee akt</a>
              </div>
              <div class="cal-sheet__more">
                <button type="button" @click="openForm">Ava töökäsk</button>
                <router-link v-if="card.data.object && card.data.object.id" :to="{ name: 'object/view/info', params: { objectId: card.data.object.id } }">Ava objekt</router-link>
              </div>
            </div>
          </v-bottom-sheet>
        </v-sheet>
      </v-col>
    </v-row>
  </loading-content>
</template>

<script>
import dayjs from 'dayjs'
import api from './../api'
import { dateRange, shortAddress } from './../plugins/utils'
import { deviceSummary } from '@/plugins/devices'
import { mapActions, mapGetters, mapState } from 'vuex'
import helpersMixin from '@/plugins/helpersMixin'
import loadArchivo from '@/plugins/archivo'
import stickyLanes from '@/plugins/calendarLanes'
import { effectiveLook, lookOf, tint, orderStyle, timeStyle, initialsStyle } from '@/plugins/calendarLooks'

// Finger: hold this long to lift a work order; moving further first is a scroll.
const LONG_PRESS = 450
const SLOP = 8
// Half an hour on a phone, where the page scrolls (pageScroll).
const PHONE_PX = 40
const QUARTER = 15 * 60 * 1000

// Guarded: a browser that refuses storage (private mode, site data blocked)
// throws on the first touch, and this file is loaded with the router — the
// whole app would fail to start over a remembered view type.
const localStorage = (() => {
  try {
    const store = window.localStorage
    store.getItem('calendarType')
    return {
      getItem: key => { try { return store.getItem(key) } catch (e) { return null } },
      setItem: (key, value) => { try { store.setItem(key, value) } catch (e) { /* not remembered */ } }
    }
  } catch (e) {
    return null
  }
})()

// Everything put into the squares and hover cards below goes through v-html,
// and all of it is somebody's typing: a name, a phone, an address, a device.
const esc = text => String(text == null ? '' : text).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

export default {
  mixins: [ helpersMixin ],
  data () {
    return {
      loading: true,
      focus: '',
      type: localStorage && localStorage.getItem('calendarType') || 'workweek',
      viewTypes: [{
        label: 'Päev',
        icon: 'mdi-calendar-range',
        key: 'day'
      }, {
        label: 'Töönädal',
        icon: 'mdi-calendar',
        key: 'workweek'
      },{
        label: 'Nädal',
        icon: 'mdi-calendar-minus',
        key: 'week'
      },{
        label: 'Kuu',
        icon: 'mdi-calendar-month',
        key: 'month'
      }],
      settings: ['showOnlyMaintenance'],
      settingOptions: [{
        value: 'showOnlyMe',
        label: 'Ainult minu töökäsud',
        short: 'Minu'
      }, {
        value: 'showOnlyMaintenance',
        label: 'Ainult hooldused',
        short: 'Hooldus'
      }, {
        value: 'showOnlyOffice',
        label: 'Ainult kontoripäev',
        short: 'Kontoris'
      }],
      selectedEvent: {},
      selectedElement: null,
      selectedOpen: false,
      dragEvent: null,
      dragStart: null,
      createEvent: null,
      createStart: null,
      extendEvent: null,
      extendOriginal: null,
      events: [],
      filteredEvents: [],
      tmpEvents: [],
      period: {
        startDate: null,
        endDate: null
      },
      start: dayjs(this.$route.query.startDate).isValid() ? dayjs(this.$route.query.startDate).toDate() : undefined,
      backgroundRefresh: null,
      // The new look's fitted grid: the sheet's height, half an hour in px,
      // and whether the evening is still below the fold.
      calHeight: null,
      intervalPx: 26,
      eveningHidden: false,
      scrollArea: null,
      // A phone: one day at a time, with the week's days as buttons.
      narrow: window.innerWidth <= 700,
      // Finger work on a work order: the one picked (outline and end handle),
      // the gesture in progress, and the last change with its way back.
      pickedId: null,
      touch: null,
      undo: null,
      undoTimer: null,
      // Kalender telefonis: Päev or Nimekiri as chosen on this phone (null:
      // the role's default), the work order shown as a card, the filter
      // sheet, and an empty evening opened by hand.
      phoneViewChoice: localStorage && localStorage.getItem('calendarPhoneView'),
      card: null,
      cardOpen: false,
      eveningOpen: false
    }
  },
  mounted () {
    this.$refs.calendar.checkChange()
    const isWeekend = new Date().getDay() % 6 == 0

    let parsed = null
    try {
      const saved = localStorage && localStorage.getItem('calendarSettings')
      parsed = saved ? JSON.parse(saved) : null
    } catch (e) {
      parsed = null
    }
    if (parsed && parsed.userId === this.user.userId && Array.isArray(parsed.settings)) {
      this.settings = parsed.settings
    } else {
      if (this.isTechnic) {
        this.settings = ['showOnlyMe']
      }
      if (this.isSecretary) {
        // Replaced rather than pushed: in Vue 3 a push does not reach the
        // settings watcher, which is what stores the choice and re-filters.
        this.settings = [...this.settings, 'showOnlyOffice']
      }
    }

    if (this.isTechnic) {
      this.setToday()
    } else if (isWeekend && this.type === 'workweek') {
      this.type = 'week'
    }
    window.addEventListener('resize', this.refit)
    this.$refs.calSheet.$el.addEventListener('touchstart', this.touchStart, { passive: true })
    this.$refs.calSheet.$el.addEventListener('contextmenu', this.noMenu)
    document.addEventListener('pointerdown', this.unpick)
    this.fit()
    this.backgroundRefresh = setInterval(() => {
      // A tab nobody is looking at fetches nothing; it catches up on return.
      if (document.hidden) return
      this.refresh(false)
    }, 60 * 1000)
    this.onVisible = () => { if (!document.hidden) this.refresh(false) }
    document.addEventListener('visibilitychange', this.onVisible)
  },
  created () {
    // The finger gesture in progress; plain, not reactive (see touchStart).
    this.gesture = null
    // A phone has been put on one day already this visit (see the plate watcher).
    this.phoneDay = false
  },
  beforeUnmount () {
    clearInterval(this.backgroundRefresh)
    document.removeEventListener('visibilitychange', this.onVisible)
    window.removeEventListener('resize', this.refit)
    document.removeEventListener('pointerdown', this.unpick)
    this.dropTouch()
    clearTimeout(this.undoTimer)
    this.watchScroll(null)
  },
  watch: {
    plate: {
      handler (on) {
        if (on) loadArchivo()
        // A phone opens on one day; the switch is only known once the
        // features are in, after mounted().
        if (on && this.narrow && !this.phoneDay && (this.type === 'week' || this.type === 'workweek')) {
          this.phoneDay = true
          this.type = 'day'
        }
      },
      immediate: true
    },
    fitHours () {
      this.fit()
    },
    // The phone layout has Päev and Nimekiri only; a week or month chosen on a
    // computer opens as the day.
    phone: {
      handler (on) {
        if (on && this.type !== 'day') this.type = 'day'
      },
      immediate: true
    },
    // A shorter or longer evening changes the grid's height.
    intervalCount () {
      this.fit()
    },
    // Across 700 px: the hours move between their own box and the page.
    pageScroll () {
      this.fit(true)
    },
    // The planner rows in the head change how much room the hours have.
    filteredEvents () {
      this.fit()
    },
    loading () {
      this.fit()
    },
    type (type) {
      this.fit(true)
      if (type === 'workweek') {
        this.start = dayjs().startOf('week').toDate()
        // A day picked in the day or month view outranks start; a weekend one
        // would break the work week the same way (see calendarStart).
        if (this.focus) {
          this.focus = this.weekdayAnchor(this.focus).format('YYYY-MM-DD')
        }
      }
      // The phone's forced day is not the computer's choice.
      if (localStorage && !this.phone) {
        localStorage.setItem('calendarType', type)
      }
    },
    params: {
      handler (params) {
        this.$router
          .push({ query: { ...params } })
          .catch(() => {})
      },
      deep: true
    },
    events () {
      this.updateEvents()
    },
    settings (val) {
      // localStorage, not sessionStorage: the choice is meant to outlive the tab,
      // the same way the view type above does.
      if (localStorage) {
        localStorage.setItem('calendarSettings', JSON.stringify({ userId: this.user.userId, settings: val }))
      }
      this.updateEvents()
    },
    // deep: 1 — Vue 3 only calls a plain array watcher when the array is
    // replaced, and this one is pushed to and spliced from. One level is the
    // array's own members; a drag moving a member's start does not need a
    // re-filter, the calendar redraws that event by itself.
    tmpEvents: {
      handler () {
        this.updateEvents()
      },
      deep: 1
    }
  },
  computed: {
    ...mapGetters(['isTechnic']),
    ...mapState(['user', 'features', 'prefs']),
    params () {
      return {
        startDate: dayjs(this.period.startDate).isValid() ? this.period.startDate : undefined
      }
    },
    // Vuetify builds a work week from the day it is anchored on: back to the
    // Monday, forward to the Friday. From a Saturday or Sunday that forward
    // search finds the *next* Friday and the view is cut at 7 days — Mon–Fri
    // plus the next Monday and Tuesday. That is what opening the calendar on
    // a weekend showed (start = today). So a work week is anchored on its
    // Monday whenever the day itself is a weekend day.
    calendarStart () {
      if (this.type !== 'workweek') return this.start
      return this.weekdayAnchor(this.start || new Date()).toDate()
    },
    // The new look, in the views with hours: working time fills the screen.
    fitHours () {
      return this.plate && this.type !== 'month'
    },
    // The new look's lanes: a technician keeps theirs all day (plugins/calendarLanes).
    laneMode () {
      return stickyLanes
    },
    dayStrip () {
      return this.plate && this.narrow && this.type !== 'month'
    },
    // Süsteem -> Seaded -> Kalender telefonis, on a phone in the new look.
    phone () {
      return this.plate && this.narrow && !!(this.features && this.features.calendarPhone)
    },
    // A technician reads the day as a list; the office sees who is where.
    phoneView: {
      get () {
        if (this.phoneViewChoice === 'day' || this.phoneViewChoice === 'list') return this.phoneViewChoice
        return this.$hasRole(['tehnic']) && !this.$hasRole(['admin', 'secretary', 'root']) ? 'list' : 'day'
      },
      set (view) {
        this.phoneViewChoice = view
        if (localStorage) localStorage.setItem('calendarPhoneView', view)
        this.fit(true)
      }
    },
    focusDate () {
      return this.period.startDate || dayjs().format('YYYY-MM-DD')
    },
    onToday () {
      return this.focusDate === dayjs().format('YYYY-MM-DD')
    },
    // The work orders of the day shown, in time order, as the filters leave them.
    dayOrders () {
      return this.filteredEvents
        .filter(event => event.timed && event.data && dayjs(event.start).format('YYYY-MM-DD') === this.focusDate)
        .sort((a, b) => a.start - b.start || String(a.data.user.fullName || '').localeCompare(String(b.data.user.fullName || ''), 'et'))
    },
    // Work orders per day of the week, for the numbers under the day buttons.
    dayCounts () {
      const counts = {}
      this.filteredEvents.forEach(event => {
        if (!event.timed || !event.data) return
        const date = dayjs(event.start).format('YYYY-MM-DD')
        counts[date] = (counts[date] || 0) + 1
      })
      return counts
    },
    dayPlanner () {
      return this.filteredEvents
        .filter(event => event.data && event.data.type == 1 && dayjs(event.start).format('YYYY-MM-DD') === this.focusDate)
        .map(event => event.data)
    },
    // "Nädal 44 · 26. okt 2026 · 7 töökäsku", as parts: a narrow phone
    // breaks the line between them, never inside one.
    phoneCaption () {
      const count = this.dayOrders.length
      return ['Nädal ' + this.isoWeek(this.focusDate), this.title, count + (count === 1 ? ' töökäsk' : ' töökäsku')]
    },
    // The evening on a phone: shown when a work order reaches into it, or when opened.
    showEvening () {
      if (!this.phone) return true
      const evening = dayjs(this.focusDate).hour(18).valueOf()
      return this.eveningOpen || this.dayOrders.some(event => +event.end > evening)
    },
    // 08:00 on: to 22:30, or to 18:00 when a phone's evening is empty.
    intervalCount () {
      return this.showEvening ? 29 : 20
    },
    cardWhen () {
      if (!this.card) return ''
      const start = dayjs(this.card.start)
      return ['P', 'E', 'T', 'K', 'N', 'R', 'L'][start.day()] + ' ' + start.format('D.MM') + ' · ' + this.hm(this.card.start) + '–' + this.hm(this.card.end)
    },
    // On a phone the whole page scrolls under the app bar, the day buttons
    // staying at the top; the hours are no box of their own (mockup C,
    // 6 Oct 2026). On a bigger screen they fill the window and scroll inside.
    pageScroll () {
      return this.dayStrip
    },
    // The week around the day shown, Monday to Sunday.
    stripDays () {
      const from = this.period.startDate ? dayjs(this.period.startDate) : dayjs()
      const monday = from.subtract((from.day() + 6) % 7, 'day')
      const today = dayjs().format('YYYY-MM-DD')
      return Array.from({ length: 7 }, (_, i) => {
        const day = monday.add(i, 'day')
        return { date: day.format('YYYY-MM-DD'), today: day.format('YYYY-MM-DD') === today, label: ['E', 'T', 'K', 'N', 'R', 'L', 'P'][i] + ' ' + day.date() }
      })
    },
    intervalHeight () {
      return this.fitHours ? this.intervalPx : 26
    },
    calendarType () {
      return this.type === 'workweek' ? 'week' : this.type
    },
    weekdays () {
      switch (this.type) {
        case 'workweek':
          return [1, 2, 3, 4, 5]
        default:
          return [1, 2, 3, 4, 5, 6, 0]
      }
    },
    title () {
      return dateRange(this.period.startDate, this.period.endDate)
    },
    // The person's own choice on Minu seaded, else Süsteem -> Seaded ->
    // Kalender uues kujunduses (plugins/calendarLooks.js).
    look () {
      return effectiveLook(this.prefs, this.features)
    },
    lookDef () {
      return lookOf(this.look)
    },
    plate () {
      return this.look !== 'vana'
    },
    // "NÄDAL 24 · TÖÖNÄDAL · AINULT HOOLDUSED" over the title.
    caption () {
      const parts = []
      const start = this.period.startDate && dayjs(this.period.startDate)
      if (start && start.isValid() && this.type !== 'month') {
        parts.push('Nädal ' + this.isoWeek(start))
      }
      const view = this.viewTypes.find(t => t.key === this.type)
      if (view) parts.push(view.label)
      this.settingOptions
        .filter(setting => this.settings.includes(setting.value))
        .forEach(setting => parts.push(setting.label.replace(' töökäsud', '')))
      return parts.join(' · ')
    },
    filters () {
      return {
        showOnlyMe: this.settings.includes('showOnlyMe'),
        showOnlyMaintenance: this.settings.includes('showOnlyMaintenance'),
        showOnlyOffice: this.settings.includes('showOnlyOffice')
      }
    },
    eventView () {
      const id = this.selectedEvent && this.selectedEvent.data && this.selectedEvent.data.id || undefined

      return {
        eventId: id,
        event: !id && this.selectedEvent || null
      }
    }
  },
  methods: {
    ...mapActions(['showNotification']),
    // A Saturday or Sunday moves back to its own week's Monday (et locale: the
    // week starts on Monday); a weekday stays as it is.
    // ISO week: the week holding the year's first Thursday is week 1.
    isoWeek (date) {
      const start = dayjs(date)
      const d = new Date(Date.UTC(start.year(), start.month(), start.date()))
      d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7))
      return Math.ceil(((d - Date.UTC(d.getUTCFullYear(), 0, 1)) / 864e5 + 1) / 7)
    },
    hm (time) {
      return dayjs(time).format('H:mm')
    },
    userColor (data) {
      return (data && data.user && data.user.color) || '#1976D2'
    },
    fullAddress (data) {
      return (data && data.object && shortAddress(data.object.fullAddress)) || 'Aadress puudub'
    },
    devicesOf (data) {
      return data && data.object ? deviceSummary(data.object.devices) : ''
    },
    // "Hooldus · Mari Maasikas · Vaillant ecoTEC"
    rowSub (data) {
      return [this.calendarCategoryById(data.category).text, data.client && data.client.fullName, this.devicesOf(data)]
        .filter(Boolean).join(' · ')
    },
    isNow (event) {
      const now = Date.now()
      return +event.start <= now && now < +event.end
    },
    telOf (data) {
      return data && data.client && data.client.phone ? String(data.client.phone).replace(/[^\d+]/g, '') : ''
    },
    mapsUrl (data) {
      return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(data.object.fullAddress)
    },
    // As the Töökäsk's own Akt button (components/Event.vue showAkt / aktUrl).
    canAkt (data) {
      return !!(this.features && this.features.akt) && data.status !== 'deleted' &&
        !!(data.object && data.object.id) && this.$hasRole(['tehnic', 'admin', 'root'])
    },
    aktUrl (data) {
      const params = { objectId: data.object.id, calendarId: data.id, clientId: data.client && data.client.id }
      return '/akt/new?' + Object.keys(params).filter(key => params[key]).map(key => key + '=' + encodeURIComponent(params[key])).join('&')
    },
    openCard (event) {
      this.card = event
      this.cardOpen = true
    },
    // From the card to the Töökäsk form, as a tap opened it before.
    openForm () {
      const event = this.card
      this.cardOpen = false
      this.selectedEvent = event
      setTimeout(() => { this.selectedOpen = true }, 10)
    },
    weekdayAnchor (date) {
      const day = dayjs(date)
      return day.day() % 6 === 0 ? day.startOf('week') : day
    },
    setType (type) {
      this.type = type
    },
    // What v-list-item-group's `multiple` did: a click adds the setting or takes
    // it away. A new array rather than a push, so the watcher below sees it.
    toggleSetting (value) {
      this.settings = this.settings.includes(value)
        ? this.settings.filter(setting => setting !== value)
        : [...this.settings, value]
    },
    updateEvents () {
      this.filteredEvents = this.events
        .filter(event => !event.workday || !event.workday.hide)
        // A deactivated technician's planner days are left out; their work
        // orders stay, they are history.
        .filter(event => !event.workday || event.user.active)
        .filter(event => !this.filters.showOnlyMe || event.workday || event.user.id === this.user.userId || (event.helper && event.helper.id === this.user.userId))
        .filter(event => !this.filters.showOnlyMaintenance || event.workday || event.category === this.calendarCategories.MAINTENENCE)
        .filter(event => !this.filters.showOnlyOffice || event.workday || this.plannerEvents(event.startDate, 2).map(e => e.user.id).includes(event.user.id))
        .map(event => {
          return {
            data: event,
            start: new Date(event.startDate),
            end: new Date(event.type === 1 ? event.startDate : event.endDate),
            color: event.user.color || '#1976D2',
            timed: event.type === 2
          }
        })
        .concat(this.tmpEvents)
    },
    async updateEventTime (event) {
      this.loading = true
      const before = { start: event.data.startDate, end: event.data.endDate }
      try {
        await api.updateEventTime(event.data.id, {
          startDate: dayjs(event.start).toISOString(),
          endDate: dayjs(event.end).toISOString()
        })
        if (this.plate) this.offerUndo(event.data.id, before, { start: +event.start, end: +event.end })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.refresh()
    },
    onNewEvent () {
      this.refresh()
      this.selectedOpen = false
    },
    onDeleteEvent () {
      this.refresh()
      this.selectedOpen = false
    },
    changed (info) {
      this.period.startDate = info.start.date
      this.period.endDate = info.end.date
      this.eveningOpen = false
      this.fit(true)
      this.refresh()
    },
    // The new look: the sheet runs to the bottom of the window and 08:00–18:00
    // (the 20 half hours after first-interval) fills what is left under the
    // day row; the evening up to 22:30 is scrolled to. Never smaller than the
    // 26 px the calendar always had, so a low window simply scrolls sooner.
    // toTop: a new period or view opens on the morning again.
    async fit (toTop = false) {
      if (!this.fitHours) {
        this.calHeight = null
        this.watchScroll(null)
        return
      }
      await this.$nextTick()
      const sheet = this.$refs.calSheet && this.$refs.calSheet.$el
      if (!sheet) return
      if (this.pageScroll) {
        // A phone: the sheet is as tall as its day, half an hour PHONE_PX.
        this.calHeight = null
        this.intervalPx = PHONE_PX
        this.watchScroll(window)
        await this.$nextTick()
        // A new day opens on its morning, if the page was already past it.
        const morning = sheet.getBoundingClientRect().top + window.scrollY - this.stickyBottom()
        if (toTop && window.scrollY > morning) window.scrollTo({ top: morning })
        this.onScroll()
        return
      }
      const top = sheet.getBoundingClientRect().top + window.scrollY
      this.calHeight = Math.max(360, window.innerHeight - top - 16)
      await this.$nextTick()
      const area = sheet.querySelector('.v-calendar-daily__scroll-area')
      this.watchScroll(area)
      if (!area) return
      this.intervalPx = Math.max(26, Math.floor(area.clientHeight / 20))
      await this.$nextTick()
      if (toTop) area.scrollTop = 0
      this.onScroll()
    },
    // Where the page is covered from above: the app bar, and on a phone the
    // day buttons stuck under it.
    stickyBottom () {
      const strip = this.$el && this.$el.querySelector && this.$el.querySelector('.cal-days')
      return strip ? strip.getBoundingClientRect().bottom : 0
    },
    refit () {
      this.narrow = window.innerWidth <= 700
      this.fit()
    },
    // ---- Finger: hold to lift a work order, drag to move it, the handle to
    // stretch it. A quick swipe stays a scroll. Mouse dragging is Vuetify's
    // (startDrag … endDrag) and untouched. ------------------------------------
    touchStart (e) {
      if (!this.plate || this.type === 'month' || e.touches.length !== 1 || this.gesture) return
      const slot = e.target.closest('.v-event-timed')
      const box = slot && slot.querySelector('[data-ev-id]')
      if (!box) return
      const id = Number(box.dataset.evId)
      const event = this.filteredEvents.find(item => item.data && item.data.id === id)
      if (!event) return
      const point = e.touches[0]
      // Touch events keep going to the element first touched even when the
      // calendar redraws it elsewhere, so they are listened for right there.
      const target = e.target
      const g = this.gesture = {
        id, event, target, kind: null,
        x0: point.clientX, y0: point.clientY, x: point.clientX, y: point.clientY,
        start: +event.start, end: +event.end,
        timer: null, frame: null
      }
      target.addEventListener('touchmove', this.touchMove, { passive: false })
      target.addEventListener('touchend', this.touchEnd, { passive: false })
      target.addEventListener('touchcancel', this.touchCancel)
      if (e.target.closest('[data-grip]')) {
        this.lift('resize')
      } else {
        g.timer = setTimeout(() => this.lift('move'), LONG_PRESS)
      }
    },
    lift (kind) {
      const g = this.gesture
      if (!g) return
      g.kind = kind
      g.grab = this.timeAt(g.x0, g.y0).time - g.start
      this.touch = { id: g.id, kind, dropDate: null }
      this.pickedId = g.id
      this.preventOpen = true
      if (navigator.vibrate) {
        try { navigator.vibrate(12) } catch (err) { /* not allowed here */ }
      }
      g.frame = requestAnimationFrame(this.edgeScroll)
    },
    touchMove (e) {
      const g = this.gesture
      if (!g) return
      const point = e.touches[0]
      g.x = point.clientX
      g.y = point.clientY
      if (!g.kind) {
        // Moved before the hold was up: a scroll, the browser's.
        if (Math.hypot(g.x - g.x0, g.y - g.y0) > SLOP) this.dropTouch()
        return
      }
      if (e.cancelable) e.preventDefault()
      this.dragTo(g.x, g.y)
    },
    dragTo (x, y) {
      const g = this.gesture
      const over = document.elementFromPoint(x, y)
      const day = over && over.closest('.cal-day')
      this.touch.dropDate = g.kind === 'move' && day ? day.dataset.date : null
      if (this.touch.dropDate) return
      const at = this.timeAt(x, y)
      if (!at) return
      const event = g.event
      const first = dayjs(at.date).hour(8).valueOf()
      const last = dayjs(at.date).hour(22).minute(30).valueOf()
      const nearest = time => Math.round(time / QUARTER) * QUARTER
      if (g.kind === 'move') {
        const length = g.end - g.start
        const start = Math.min(Math.max(nearest(at.time - g.grab), first), last - length)
        event.start = start
        event.end = start + length
      } else {
        event.end = Math.min(Math.max(nearest(at.time), +event.start + QUARTER), last)
      }
    },
    // The day column under the finger and the time at its height.
    timeAt (x, y) {
      const sheet = this.$refs.calSheet && this.$refs.calSheet.$el
      const columns = sheet ? Array.from(sheet.querySelectorAll('.v-calendar-daily__day')) : []
      const column = columns.find(col => {
        const box = col.getBoundingClientRect()
        return x >= box.left && x < box.right
      }) || (columns.length === 1 ? columns[0] : null)
      const mark = column && column.querySelector('.cal-day-mark')
      if (!mark) return null
      const minutes = 8 * 60 + (y - column.getBoundingClientRect().top) / this.intervalHeight * 30
      return { date: mark.dataset.date, time: dayjs(mark.dataset.date).startOf('day').add(minutes, 'minute').valueOf() }
    },
    // Near the top or bottom of the hours the grid scrolls on under the finger.
    edgeScroll () {
      const g = this.gesture
      if (!g || !g.kind) return
      const area = this.scrollArea
      if (area) {
        // The page on a phone: its edges are under the day buttons and the
        // bottom of the screen.
        const box = area === window ? { top: this.stickyBottom(), bottom: window.innerHeight } : area.getBoundingClientRect()
        // Over the day buttons themselves the finger is choosing a day, not scrolling.
        const step = g.y < box.top ? 0 : g.y < box.top + 48 ? -8 : g.y > box.bottom - 48 ? 8 : 0
        if (step) {
          if (area === window) window.scrollBy(0, step)
          else area.scrollTop += step
          this.dragTo(g.x, g.y)
        }
      }
      g.frame = requestAnimationFrame(this.edgeScroll)
    },
    touchEnd (e) {
      const g = this.gesture
      if (!g) return
      if (!g.kind) {
        // A tap: the click that follows opens the Töökäsk as ever.
        this.dropTouch()
        return
      }
      if (e.cancelable) e.preventDefault()
      const event = g.event
      const dropDate = this.touch && this.touch.dropDate
      if (dropDate) {
        // Let go over a day button: its own hours on that day, whatever times
        // the finger passed over on the way there.
        const start = dayjs(g.start)
        const moved = dayjs(dropDate).hour(start.hour()).minute(start.minute()).second(0).millisecond(0).valueOf()
        event.end = moved + (g.end - g.start)
        event.start = moved
      }
      const changed = +event.start !== g.start || +event.end !== g.end
      this.dropTouch()
      setTimeout(() => { this.preventOpen = false }, 400)
      if (changed) this.updateEventTime(event)
    },
    touchCancel () {
      const g = this.gesture
      if (g && g.kind) {
        g.event.start = g.start
        g.event.end = g.end
      }
      this.dropTouch()
    },
    dropTouch () {
      const g = this.gesture
      if (g) {
        clearTimeout(g.timer)
        cancelAnimationFrame(g.frame)
        g.target.removeEventListener('touchmove', this.touchMove)
        g.target.removeEventListener('touchend', this.touchEnd)
        g.target.removeEventListener('touchcancel', this.touchCancel)
      }
      this.gesture = null
      this.touch = null
    },
    // A touch anywhere but on a work order lets go of the picked one.
    unpick (e) {
      if (this.pickedId != null && !(e.target.closest && e.target.closest('.cal-ev'))) {
        this.pickedId = null
      }
    },
    // A long press must not bring up the phone's own menu over a work order.
    noMenu (e) {
      if (e.target.closest && e.target.closest('.cal-ev')) e.preventDefault()
    },
    // The time row: in some looks the technician's full colour, text dark or light to suit it.
    plateTimeStyle (event) {
      return timeStyle(this.lookDef, event.data && event.data.user && event.data.user.color)
    },
    initialsStyle,
    // After a move or stretch: say what happened and offer the way back.
    offerUndo (id, before, after) {
      const sameLength = (after.end - after.start) === (+new Date(before.end) - +new Date(before.start))
      const day = dayjs(after.start)
      this.undo = {
        id,
        before,
        text: (sameLength ? 'Teisaldatud ' : 'Muudetud ') + ['P', 'E', 'T', 'K', 'N', 'R', 'L'][day.day()] + ' ' + day.format('D.MM') + ' ' +
          day.format('HH:mm') + '–' + dayjs(after.end).format('HH:mm')
      }
      clearTimeout(this.undoTimer)
      this.undoTimer = setTimeout(() => { this.undo = null }, 8000)
    },
    async undoMove () {
      const undo = this.undo
      this.undo = null
      clearTimeout(this.undoTimer)
      if (!undo) return
      this.loading = true
      try {
        await api.updateEventTime(undo.id, {
          startDate: dayjs(undo.before.start).toISOString(),
          endDate: dayjs(undo.before.end).toISOString()
        })
      } catch (err) {
        this.showNotification({ text: err.message, color: 'red' })
      }
      this.refresh()
    },
    watchScroll (area) {
      if (this.scrollArea === area) return
      if (this.scrollArea) this.scrollArea.removeEventListener('scroll', this.onScroll)
      this.scrollArea = area
      if (area) area.addEventListener('scroll', this.onScroll, { passive: true })
    },
    onScroll () {
      const area = this.scrollArea === window ? document.scrollingElement : this.scrollArea
      this.eveningHidden = !!area && area.scrollTop + area.clientHeight < area.scrollHeight - 4
    },
    toEvening () {
      const area = this.scrollArea
      if (area === window) window.scrollTo({ top: document.scrollingElement.scrollHeight, behavior: 'smooth' })
      else if (area) area.scrollTo({ top: area.scrollHeight, behavior: 'smooth' })
    },
    async refresh (showLoader = true) {
      // The minute's background refresh waits while a work order is in hand.
      if (!showLoader && this.gesture) return
      // Numbered, so that stepping through weeks quickly cannot end on the
      // answer for a week already left behind.
      const request = this.refreshRequest = (this.refreshRequest || 0) + 1
      try {
        if (showLoader) {
          this.loading = true
        }
        // A phone asks for the whole week: the day buttons count its orders.
        const range = this.phone && this.stripDays.length
          ? { startDate: this.stripDays[0].date, endDate: this.stripDays[6].date }
          : this.period
        const { events } = await api.getEvents({
          ...range
        })
        if (request === this.refreshRequest) {
          this.events = events
        }
      } catch (err) {
        // The minute's background refresh fails quietly and tries again; a week
        // somebody asked for must not come up empty as if there were no work.
        if (showLoader && request === this.refreshRequest) {
          this.$store.dispatch('showNotification', { text: 'Kalendrit ei saanud laadida: ' + ((err && err.message) || 'ühenduse viga'), color: 'red' })
        }
      } finally {
        if (showLoader) {
          this.loading = false
        }
      }
    },
    viewDay (nativeEvent, { date }) {
      this.focus = date
      this.setType('day')
    },
    getEventColor (event) {
      // The new look draws its own tint inside the box.
      if (this.plate) return 'transparent'
      return event.color
    },
    // The hover card in the event's own colour. A technician's colour is a hex
    // value and goes in as a style; a named one ('green' for a planner day)
    // goes in as Vuetify's bg-* class, which brings its own text colour.
    isCssColor (color) {
      return /^(#|rgb|hsl)/i.test(color || '')
    },
    tooltipClass (event) {
      if (this.plate) return ['event-tooltip', 'cal-tip']
      const color = this.getEventColor(event)
      return ['event-tooltip', color && !this.isCssColor(color) ? 'bg-' + color : null]
    },
    tooltipStyle (event) {
      if (this.plate) return {}
      const color = this.getEventColor(event)
      return this.isCssColor(color) ? { backgroundColor: color, color: '#fff' } : {}
    },
    // The technician's initials for the badge on a work order.
    initials (user) {
      return String((user && user.fullName) || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase()
    },
    tint,
    plateEventStyle (event) {
      const data = event.data
      if (!data) {
        // A work order being drawn on empty time.
        return { background: 'rgba(234,243,234,.85)', boxShadow: 'inset 0 0 0 1.5px #2E7D32' }
      }
      if (data.type == 1) {
        return { background: this.tint('#' + String((data.workday && data.workday.color) || 'C9D2CE').replace('#', ''), 0.24) }
      }
      return orderStyle(this.lookDef, (data.user && data.user.color) || '#1976D2')
    },
    // The work order's lines after the time: address, client, devices — as
    // body() has them, one per line, cut at the box's edge.
    plateLines (event) {
      const data = event.data
      if (!data) return '<div class="event-line"><b>uus</b></div>'
      // Most looks have no name line: the initials say who, the hover card has the name.
      const lines = []
      if (this.lookDef.nameLine && data.user) lines.push(data.helper && data.helper.fullName ? data.user.fullName + ', ' + data.helper.fullName : data.user.fullName)
      lines.push(shortAddress(data.object.fullAddress))
      if (data.client) lines.push(data.client.fullName + ' (' + data.client.phone + ')')
      const devices = deviceSummary(data.object.devices)
      if (devices) lines.push(devices)
      const adr = this.lookDef.nameLine && data.user ? 1 : 0
      return lines.map((line, i) => '<div class="event-line' + (i === adr ? ' cal-adr' : '') + '">' + esc(line) + '</div>').join('')
    },
    setToday () {
      this.start = dayjs().toDate()
      this.focus = ''
    },
    prev () {
      this.$refs.calendar.prev()
    },
    next () {
      this.$refs.calendar.next()
    },
    showEvent (nativeEvent, { event }) {
      if (event.data && event.data.workday) {
        return
      }

      if (this.preventOpen) {
        return
      }

      if (this.phone && event.data) {
        this.openCard(event)
        nativeEvent.stopPropagation()
        return
      }

      const open = () => {
        this.selectedEvent = event
        this.selectedElement = nativeEvent.target
        setTimeout(() => this.selectedOpen = true, 10)
      }

      if (this.selectedOpen) {
        this.selectedOpen = false
        setTimeout(open, 10)
      } else {
        open()
      }

      nativeEvent.stopPropagation()
    },
    rnd (a, b) {
      return Math.floor((b - a + 1) * Math.random()) + a
    },
    formatInterval (date) {
      return date.time
    },
    formatWeekday (date) {
      // The new look has the day's number large under it already.
      return dayjs(date.date).format(this.plate ? 'ddd' : 'ddd MM/DD')
    },
    // The street of a work order, without the town: what fits on a month line.
    shortStreet (event) {
      return String(shortAddress(event.data.object.fullAddress) || '').split(',')[0]
    },
    startDrag (nativeEvent, { event, timed }) {
      if (event && timed) {
        this.dragEvent = event
        this.dragTime = null
        this.extendOriginal = null
        this.extendEvent = null
      }
    },
    endDrag () {
      let event = this.dragEvent || this.extendEvent

      if (this.createEvent) {
        this.selectedEvent = this.createEvent
        this.selectedOpen = true
        const i = this.tmpEvents.indexOf(this.createEvent)
        if (i !== -1) {
          this.tmpEvents.splice(i, 1)
        }
      } else if (event) {
        const hasChanged = !(dayjs(event.start).isSame(dayjs(event.data.startDate)) && dayjs(event.end).isSame(dayjs(event.data.endDate)))
        if (hasChanged) {
          this.updateEventTime(event)
          this.preventOpen = true
        }
      }

      this.dragTime = null
      this.dragEvent = null
      this.createEvent = null
      this.createStart = null
      this.extendOriginal = null
      this.extendEvent = null

      setTimeout(() => {
        this.preventOpen = false
      }, 10)
    },
    cancelDrag () {
      if (this.createEvent) {
        if (this.extendOriginal) {
          this.createEvent.end = this.extendOriginal
        } else {
          const i = this.tmpEvents.indexOf(this.createEvent)
          if (i !== -1) {
            this.tmpEvents.splice(i, 1)
          }
        }
      }

      this.createEvent = null
      this.createStart = null
      this.dragTime = null
      this.dragEvent = null
    },
    startTime (nativeEvent, tms) {
      const mouse = this.toTime(tms)

      if (this.dragEvent && this.dragTime === null) {
        const start = this.dragEvent.start

        this.dragTime = mouse - start
      } else {
        this.createStart = this.roundTime(mouse)
        this.createEvent = {
          color: 'green',
          start: this.createStart,
          end: this.createStart,
          timed: true,
          data: null
        }

        this.tmpEvents.push(this.createEvent)
      }
    },
    mouseMove (nativeEvent, tms) {
      const mouse = this.toTime(tms)
      const other = this.createEvent || this.extendEvent

      if (this.dragEvent && this.dragTime !== null) {
        const start = this.dragEvent.start
        const end = this.dragEvent.end
        const duration = end - start
        const newStartTime = mouse - this.dragTime
        const newStart = this.roundTime(newStartTime)
        const newEnd = newStart + duration

        this.dragEvent.start = newStart
        this.dragEvent.end = newEnd
      } else if (other && this.createStart !== null) {
        const mouseRounded = this.roundTime(mouse, false)
        const min = Math.min(mouseRounded, this.createStart)
        const max = Math.max(mouseRounded, this.createStart)

        other.start = min
        other.end = max
      }
    },
    toTime (tms) {
      return new Date(tms.year, tms.month - 1, tms.day, tms.hour, tms.minute).getTime()
    },
    roundTime (time, down = true) {
      const roundTo = 15
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - time % roundDownTime
        : time + (roundDownTime - (time % roundDownTime))
    },
    extendBottom (event) {
      this.extendEvent = event
      this.createStart = event.start
      this.extendOriginal = event.end
    },
    plannerEvents (date, workdayType = false) {
      return this.events
        .filter(event => event.workday && dayjs(event.startDate).isSame(date, 'day') && event.user && event.user.active)
        .filter(event => !workdayType || event.workday.id === workdayType)
    },
    body (event, eventParsed, tooltip = false) {
      const html = []
      if (event.data && event.data.type == 1) {
        if (tooltip) {
          // The whole day, every kind of day, one line per kind: who is in the
          // office, on call, on holiday, … (the row above shows only some kinds).
          const byWorkday = {}
          this.plannerEvents(event.data.startDate).forEach(event => {
            const group = byWorkday[event.workday.id] || (byWorkday[event.workday.id] = { name: esc(event.workday.name), people: [] })
            group.people.push(esc(event.user.fullName))
          })
          Object.keys(byWorkday)
            .sort((a, b) => a - b)
            .forEach(id => {
              const { name, people } = byWorkday[id]
              html.push('<b>' + name + ':</b> ' + people.sort((a, b) => a.localeCompare(b, 'et')).join(', '))
            })
        } else {
          html.push(esc(event.data.user.fullName + ' (' + event.data.workday.name + ')'))
        }
      } else {
        html.push(esc(eventParsed.start.time + ' - ' + eventParsed.end.time))
        if (event.data) {
          if (event.data.user) {
            html.push(esc(event.data.user.fullName))
          }
          if (event.data.helper && event.data.helper.fullName) {
            html.push(esc(event.data.helper.fullName))
          }
          html.push(esc(shortAddress(event.data.object.fullAddress)))
          if (event.data.client) {
            html.push(esc(event.data.client.fullName + ' (' + event.data.client.phone + ')'))
          }
          // What is installed there, so the technician reads the equipment off
          // the calendar rather than opening the work order for it.
          const devices = deviceSummary(event.data.object.devices)
          if (devices) {
            html.push(esc(devices))
          }
        }
      }

      // In the square, one line per piece, each cut at the square's edge (as
      // Vuetify 2 drew it); the hover card has room for them whole.
      if (tooltip) {
        return html.join('<br />')
      }
      return html.map(line => '<div class="event-line">' + line + '</div>').join('')
    }
  }
}
</script>

<style lang="scss">
.event {
  line-height: 14px;
  height: 100%;
  overflow: hidden;
}
/* Vuetify 2's `.v-calendar .v-event-timed` clipped what did not fit; Vuetify 3's
   lets the text run out of the square over its neighbours. */
.v-calendar .v-event-timed {
  overflow: hidden;
}
.event-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.event-tooltip {
  opacity: 1;
}
.v-calendar-weekly__day {
  .v-event {
    height: auto !important;
  }
}
.v-event-draggable {
  padding-left: 6px;
}
.v-event-timed {
  user-select: none;
  -webkit-user-select: none;  
}
.v-event-drag-bottom {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 4px;
  cursor: ns-resize;

  &::after {
    display: none;
    position: absolute;
    left: 50%;
    height: 4px;
    border-top: 1px solid white;
    border-bottom: 1px solid white;
    width: 16px;
    margin-left: -8px;
    opacity: 0.8;
    content: '';
  }

  &:hover::after {
    display: block;
  }
}
.v-calendar-daily__day {
  .v-calendar-daily__day-interval:nth-child(3),
  .v-calendar-daily__day-interval:nth-child(6),
  .v-calendar-daily__day-interval:nth-child(9),
  .v-calendar-daily__day-interval:nth-child(13),
  .v-calendar-daily__day-interval:nth-child(17) {
    border-top: 1px solid red !important;
  }
}
.synced {
  position: absolute !important;
  top: 2px;
  right: 2px;
  font-size: 12px !important;
}

/* ---- Kalender uues kujunduses (Seaded switch calendarPlate) -------------
   Tokens as in styles/frame.css; everything starts from .cal-plate so the
   original calendar never meets a line of it. */
.cal-plate {
  --p-ground: #F4F6F5; --p-ink: #16322A; --p-ink-2: #3E504A; --p-ink-3: #5B6B66;
  --p-line: #C9D2CE; --p-line-soft: #E1E7E4; --p-line-strong: #9AAAA4;
  --p-green: #2E7D32; --p-green-dark: #1B5E20; --p-green-bg: #EAF3EA;
  --p-flame: #1F5FD1; --p-flame-bg: #EEF3FC;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-variant-numeric: tabular-nums;
  color: var(--p-ink);
}
.cal-plate .cal-wide { font-stretch: 125%; }
.cal-plate .cal-mid { font-stretch: 112%; }
/* Sized by its own width, not the window's: the same screen is wide enough
   with the menu hidden and too narrow with it shown. */
.cal-head { container-type: inline-size; margin-bottom: 12px; }
.cal-head__row { display: grid; grid-template-columns: 1fr auto 1fr; align-items: end; gap: 16px; }
.cal-head__text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.cal-head__chips { display: flex; align-items: center; justify-content: center; gap: 8px; padding-bottom: 3px; }
.cal-head__ctl { display: flex; align-items: center; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
@container (max-width: 1320px) {
  .cal-head__row { display: flex; flex-wrap: wrap; gap: 10px; }
  .cal-head__text { flex-grow: 1; }
  .cal-head__chips { order: 3; width: 100%; justify-content: flex-start; flex-wrap: wrap; padding-bottom: 0; }
}
.cal-cap { font-size: 12.5px; font-weight: 600; letter-spacing: .05em; text-transform: uppercase; color: var(--p-ink-3); }
.cal-title { margin: 0; font-size: 28px; font-weight: 700; line-height: 1.15; color: var(--p-ink); }
.cal-nav {
  width: 38px; height: 38px; border: 1.5px solid var(--p-line); border-radius: 6px; background: #fff;
  display: inline-flex; align-items: center; justify-content: center; color: var(--p-ink); cursor: pointer;
}
.cal-today {
  height: 38px; padding: 0 14px; border: 1.5px solid var(--p-green); border-radius: 6px; background: #fff;
  color: var(--p-green); font: inherit; font-weight: 700; font-size: 14px; cursor: pointer;
}
.cal-seg { display: inline-flex; border: 1.5px solid var(--p-line); border-radius: 7px; overflow: hidden; background: #fff; }
.cal-seg button {
  height: 35px; padding: 0 13px; border: 0; background: none; font: inherit; font-size: 13.5px; font-weight: 600;
  color: var(--p-ink-2); cursor: pointer;
}
.cal-seg button[aria-pressed="true"] { background: var(--p-ink); color: #fff; }
.cal-chip {
  height: 32px; padding: 0 12px; border-radius: 16px; border: 1.5px solid var(--p-line); background: #fff;
  font: inherit; font-size: 13px; font-weight: 600; color: var(--p-ink-2); display: inline-flex; align-items: center; gap: 4px; cursor: pointer;
}
.cal-chip[aria-pressed="true"] { background: var(--p-green-bg); border-color: var(--p-green); color: var(--p-green-dark); }
.cal-plate :focus-visible { outline: 3px solid var(--p-flame); outline-offset: 2px; }

/* The grid: a white card with soft lines. */
.cal-plate .v-calendar {
  border: 1px solid var(--p-line) !important; border-radius: 12px; overflow: hidden; background: #fff;
  font-family: inherit; color: var(--p-ink);
}
.cal-plate .v-calendar-daily__head,
.cal-plate .v-calendar-daily__intervals-head { border-color: var(--p-line) !important; }
.cal-plate .v-calendar-daily_head-day,
.cal-plate .v-calendar-daily__day,
.cal-plate .v-calendar-daily__intervals-body { border-color: var(--p-line-soft) !important; }
.cal-plate .v-calendar-daily_head-weekday { font-size: 12px; font-weight: 700; letter-spacing: .06em; color: var(--p-ink-3) !important; text-transform: uppercase; }
.cal-plate .v-calendar-daily_head-day-label .v-btn { font-family: inherit; font-stretch: 125%; font-weight: 700; font-size: 18px; color: var(--p-ink); }
.cal-plate .v-calendar-daily_head-day.v-present .v-calendar-daily_head-day-label .v-btn { background: var(--p-flame) !important; color: #fff !important; }
.cal-plate .v-calendar-daily_head-day.v-present,
.cal-plate .v-calendar-daily__day.v-present { background: #F7F9FE; }
.cal-plate .v-calendar-daily__day-interval { border-top-color: #F0F3F1 !important; }
.cal-plate .v-calendar-daily__day-interval:nth-child(even) { border-top-color: var(--p-line-soft) !important; }
.cal-plate .v-calendar-daily__interval-text { font-size: 11px; font-weight: 600; color: var(--p-ink-3); }
/* The visit-slot lines, kept, in a softer red. */
.cal-plate .v-calendar-daily__day {
  .v-calendar-daily__day-interval:nth-child(3),
  .v-calendar-daily__day-interval:nth-child(6),
  .v-calendar-daily__day-interval:nth-child(9),
  .v-calendar-daily__day-interval:nth-child(13),
  .v-calendar-daily__day-interval:nth-child(17) {
    border-top: 1px solid #F3B4AE !important;
  }
}
/* The fitted grid: the evening (18:00 on, the 21st half hour) lightly
   hatched, its hours in amber, and the button that scrolls down to it. */
.cal-fit { position: relative; }
.cal-plate .v-calendar-daily__day-interval:nth-child(n+21) {
  background: repeating-linear-gradient(135deg, rgba(91, 107, 102, .05) 0 6px, transparent 6px 12px);
}
.cal-plate .v-calendar-daily__interval:nth-child(n+21) .v-calendar-daily__interval-text { color: #8F5400; }
.cal-evening {
  position: absolute; left: 50%; bottom: 10px; transform: translateX(-50%); z-index: 2;
  font: inherit; font-size: 12px; font-weight: 700; color: var(--p-ink-2); background: #fff;
  border: 1.5px solid var(--p-line); border-radius: 14px; padding: 4px 11px; cursor: pointer;
  box-shadow: 0 4px 14px rgba(22, 50, 42, .12);
}
.cal-evening:hover { border-color: var(--p-line-strong); color: var(--p-ink); }
.cal-plate .v-calendar-weekly__head-weekday { font-size: 12px; font-weight: 700; letter-spacing: .06em; color: var(--p-ink-3) !important; }
.cal-plate .v-calendar-weekly__day { border-color: var(--p-line-soft) !important; }
.cal-plate .v-calendar-weekly__day.v-present { background: #F7F9FE; }
.cal-plate .v-calendar-weekly__day-label .v-btn { font-family: inherit; font-weight: 700; }

/* A work order: tint, edge, initials. */
.cal-plate .v-event-timed, .cal-plate .v-event { border: 0 !important; border-radius: 6px !important; }
.cal-plate .cal-ev {
  color: #0F241E; border-radius: 6px; padding: 4px 7px 4px 9px; font-size: 13px; line-height: 16px;
  /* A long press lifts it: no text selection, no phone menu. */
  -webkit-user-select: none; user-select: none; -webkit-touch-callout: none;
}
/* Teine tehnik: overlaps the first, so a narrow block keeps room for the time. */
.cal-ini--second { margin-left: -10px; box-shadow: 0 0 0 1px rgba(255,255,255,.8); }

/* The time row in the technician's full colour, edge to edge. */
.cal-plate .cal-ev__time { display: flex; align-items: center; gap: 5px; font-weight: 700; margin: -4px -7px 3px -9px; padding: 3px 7px 3px 9px; }
.cal-plate .cal-adr { font-weight: 650; }
.cal-plate .cal-ini.cal-ini--on { width: 20px; height: 20px; border-radius: 10px; font-size: 9.5px; background: rgba(255, 255, 255, .92); }
.cal-plate .cal-ev__time .cal-g { color: inherit; }
/* The other looks (plugins/calendarLooks.js; the rules above are 'a'). Their
   time row is not filled, so it keeps to the box's padding. */
.cal-look-praegu .cal-ev__time, .cal-look-c .cal-ev__time, .cal-look-d .cal-ev__time { margin: 0; padding: 0; }
.cal-look-praegu .cal-ev__time .cal-g, .cal-look-c .cal-ev__time .cal-g, .cal-look-d .cal-ev__time .cal-g { color: var(--p-flame); }
.cal-look-c .cal-ev__time .cal-ini, .cal-look-d .cal-ev__time .cal-ini { width: 20px; height: 20px; border-radius: 10px; font-size: 9.5px; }
/* Kahvatu: the first new look, small and pale, the name on its own line. */
.cal-look-praegu .cal-ev { color: var(--p-ink); padding: 3px 6px 3px 8px; font-size: 11.5px; line-height: 14px; }
.cal-look-praegu .cal-adr { font-weight: inherit; }
/* Joon üleval: room under the top line. Jooned külgedel: room by the right one. */
.cal-look-c .cal-ev { padding-top: 6px; }
.cal-look-d .cal-ev { padding-right: 9px; }
/* Picked by finger: an outline and the end handle. Lifted: it follows the finger. */
.cal-plate .cal-ev--picked { outline: 2.5px solid var(--p-ink); outline-offset: 1px; overflow: visible; }
.cal-plate .v-event-timed:has(.cal-ev--picked) { overflow: visible !important; z-index: 5; }
.cal-plate .v-event-timed:has(.cal-ev--lifted) { z-index: 20; }
/* The mouse's 4 px end strip would take the finger meant for the handle. */
.cal-plate .v-event-timed:has(.cal-ev--picked) .v-event-drag-bottom { display: none; }
.cal-plate .cal-ev--lifted { transform: scale(1.03); box-shadow: 0 12px 28px rgba(22, 50, 42, .35) !important; opacity: .96; }
.cal-grip {
  position: absolute; left: 50%; bottom: -14px; transform: translateX(-50%); width: 44px; height: 28px;
  display: flex; align-items: center; justify-content: center; touch-action: none;
}
.cal-grip i { display: block; position: relative; width: 28px; height: 14px; border-radius: 7px; background: var(--p-ink); box-shadow: 0 0 0 2px #fff; }
.cal-grip i::before, .cal-grip i::after { content: ''; position: absolute; left: 8px; right: 8px; height: 1.5px; background: #fff; }
.cal-grip i::before { top: 4.5px; }
.cal-grip i::after { bottom: 4.5px; }
/* A phone: the week's days as buttons over the calendar. */
.cal-days { display: flex; gap: 5px; margin-bottom: 8px; }
/* A phone (pageScroll): the page scrolls, not the hours. The day buttons stay
   under the app bar; the hours grow to their full height. */
.cal-page .cal-days {
  /* Above a lifted work order (z-index 20), which no box clips any more. */
  position: sticky; top: var(--v-layout-top, 56px); z-index: 30; background: var(--p-ground);
  margin: 0 -12px 8px; padding: 8px 12px; box-shadow: 0 6px 10px -8px rgba(22, 50, 42, .35);
}
.cal-page .v-calendar-daily, .cal-page .v-calendar-daily__body, .cal-page .v-calendar-daily__scroll-area { overflow: visible; height: auto; }
.cal-page .cal-evening { position: fixed; bottom: calc(14px + env(safe-area-inset-bottom, 0px)); z-index: 5; }
.cal-day {
  flex: 1; min-width: 0; height: 40px; border: 1.5px solid var(--p-line); border-radius: 8px; background: #fff;
  font: inherit; font-size: 13.5px; font-weight: 700; color: var(--p-ink-2); white-space: nowrap;
}
.cal-day--today { color: var(--p-flame); border-color: var(--p-flame); }
.cal-day[aria-pressed="true"] { background: var(--p-ink); border-color: var(--p-ink); color: #fff; }
.cal-day--drop { background: var(--p-green-bg) !important; border-color: var(--p-green) !important; color: var(--p-green-dark) !important; transform: scale(1.06); }
/* After a move: what happened, and the way back. */
.cal-undo {
  position: fixed; left: 50%; bottom: max(18px, env(safe-area-inset-bottom, 0px)); transform: translateX(-50%); z-index: 2000;
  display: flex; align-items: center; gap: 14px; max-width: calc(100vw - 32px);
  background: var(--p-ink); color: #fff; border-radius: 10px; padding: 10px 12px 10px 16px; font-size: 14px;
  box-shadow: 0 10px 30px rgba(22, 50, 42, .3);
}
.cal-undo button { border: 0; background: rgba(255, 255, 255, .14); color: #fff; font: inherit; font-weight: 700; border-radius: 7px; padding: 7px 11px; cursor: pointer; }
.cal-plate .cal-ev:has(.cal-ev__month) { padding: 0 6px 0 7px; line-height: 18px; }
.cal-plate .cal-ini {
  width: 18px; height: 18px; border-radius: 9px; color: #fff; font-size: 9px; font-weight: 700; flex: none;
  display: inline-flex; align-items: center; justify-content: center;
}
.cal-plate .cal-g { margin-left: auto; font-size: 10px; font-weight: 700; color: var(--p-flame); }
/* A planner row: initials and the day's word. */
.cal-plate .cal-ev--plan { display: flex; align-items: center; gap: 5px; padding: 1px 6px 1px 2px; height: 20px; font-weight: 600; white-space: nowrap; overflow: hidden; }
.cal-plate .cal-ev--plan .cal-ini { width: 16px; height: 16px; font-size: 8px; }
/* The hover card: white, all the lines, in the plate's ink. */
.cal-tip {
  background: #fff !important; color: #16322A !important; border: 1px solid #9AAAA4; border-radius: 10px !important;
  box-shadow: 0 14px 40px rgba(22, 50, 42, .22); padding: 10px 14px !important;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 13.5px !important; line-height: 19px;
}
/* ---- Kalender telefonis (Seaded switch calendarPhone) ---------------------
   ≤ 700 px in the new look: one-row head, Päev / Nimekiri, the day's
   planner as chips, the list, and the bottom sheets (which live outside
   .cal-plate, so they bring their own tokens). */
.cal-phead { display: grid; gap: 8px; margin-bottom: 8px; }
.cal-phead__row { display: flex; align-items: center; gap: 8px; }
.cal-phead__row .cal-seg { margin-right: auto; }
.cal-phead__cap { flex: 1; text-align: center; font-size: 11px; font-weight: 700; letter-spacing: .03em; line-height: 1.35; }
.cal-phead__cap span { white-space: nowrap; }
.cal-phead .cal-nav { width: 36px; height: 36px; flex: none; }
.cal-phead__tools { gap: 6px; }
.cal-phead__tools .cal-seg { flex: none; }
.cal-ftgl { display: flex; gap: 4px; min-width: 0; }
.cal-ftg {
  height: 30px; padding: 0 8px; border-radius: 15px; border: 1.5px solid var(--p-line); background: #fff;
  font: inherit; font-size: 12px; font-weight: 600; color: var(--p-ink-2); white-space: nowrap;
}
.cal-ftg[aria-pressed="true"] { border-color: var(--p-green); background: var(--p-green-bg); color: var(--p-green-dark); }
.cal-phone .cal-today { height: 34px; padding: 0 12px; font-size: 13px; }
.cal-phone .cal-seg button { height: 30px; padding: 0 9px; font-size: 12.5px; }
.cal-phone .cal-day { height: 44px; display: flex; flex-direction: column; align-items: center; justify-content: center; line-height: 1.1; font-size: 12.5px; }
.cal-day__n { font-style: normal; font-size: 10.5px; font-weight: 700; color: var(--p-flame); min-height: 12px; }
.cal-day[aria-pressed="true"] .cal-day__n { color: #fff; }
/* The day buttons say which day it is; the grid's own head would say it again. */
.cal-phone .v-calendar-daily__head { display: none; }
.cal-pchips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.cal-pchip {
  display: inline-flex; align-items: center; gap: 5px; height: 26px; padding: 0 9px 0 3px; border: 1px solid var(--p-line);
  border-radius: 13px; background: #fff; font-size: 12px; font-weight: 600; color: var(--p-ink-2); white-space: nowrap;
}
.cal-pchip .cal-ini { width: 20px; height: 20px; border-radius: 10px; font-size: 9px; }
.cal-eve {
  display: block; width: 100%; margin-top: 8px; padding: 9px 12px; border: 1px dashed #8F5400; border-radius: 8px;
  background: #FFF4E0; color: #5B3700; font: inherit; font-size: 13px; font-weight: 600;
}
/* Nimekiri: the grid stays mounted (it keeps the dates) but is not drawn. */
.cal-listmode .v-calendar, .cal-listmode .cal-eve { display: none; }
.cal-list { display: grid; gap: 6px; }
.cal-row { display: grid; grid-template-columns: 52px 1fr; gap: 8px; border: 0; background: none; padding: 0; text-align: left; font: inherit; color: inherit; }
.cal-row__t { display: grid; align-content: start; padding-top: 8px; line-height: 1.15; font-stretch: 125%; }
.cal-row__t b { font-size: 15px; font-weight: 750; }
.cal-row__t small { font-size: 11.5px; font-weight: 600; color: var(--p-ink-3); }
.cal-row--now .cal-row__t b { color: var(--p-flame); }
.cal-row__b {
  display: flex; align-items: center; gap: 9px; min-width: 0; min-height: 56px; padding: 8px 10px;
  background: #fff; border: 1px solid var(--p-line); border-left: 4px solid; border-radius: 8px;
}
.cal-row__b .cal-ini { width: 24px; height: 24px; border-radius: 12px; font-size: 10px; }
.cal-row__txt { display: grid; min-width: 0; }
.cal-row__txt b { font-size: 15px; font-weight: 600; line-height: 1.25; }
.cal-row__txt small { font-size: 12.5px; color: var(--p-ink-2); line-height: 1.3; }
.cal-list__empty { margin: 16px 0; text-align: center; color: var(--p-ink-3); font-size: 14px; }
.cal-sheet {
  --p-ink: #16322A; --p-ink-2: #3E504A; --p-ink-3: #5B6B66; --p-line: #C9D2CE; --p-line-soft: #E1E7E4;
  --p-green: #2E7D32; --p-green-dark: #1B5E20; --p-green-bg: #EAF3EA; --p-flame: #1F5FD1; --p-flame-bg: #EEF3FC;
  font-family: 'Archivo Variable', 'Archivo', 'Helvetica Neue', Helvetica, Arial, sans-serif; font-variant-numeric: tabular-nums; color: var(--p-ink);
  background: #fff; border-radius: 16px 16px 0 0; display: grid; gap: 14px;
  padding: 16px max(16px, env(safe-area-inset-right, 0px)) calc(18px + env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px));
}
.cal-sheet__head { display: grid; gap: 3px; padding-bottom: 10px; border-bottom: 2px solid var(--p-ink); }
.cal-sheet__when { font-size: 12px; font-weight: 700; letter-spacing: .05em; color: var(--p-flame); font-stretch: 112%; }
.cal-sheet__adr { font-size: 21px; font-weight: 750; line-height: 1.15; font-stretch: 125%; }
.cal-sheet__pill { justify-self: start; margin-top: 4px; font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 999px; background: var(--p-flame-bg); color: #2B3F66; }
.cal-kv { margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
.cal-kv div { display: grid; gap: 2px; min-width: 0; }
.cal-kv dt { font-size: 11px; font-weight: 700; letter-spacing: .07em; text-transform: uppercase; color: var(--p-ink-3); }
.cal-kv dd { margin: 0; font-size: 15px; font-weight: 600; overflow-wrap: anywhere; }
.cal-kv dd .cal-ini { width: 20px; height: 20px; border-radius: 10px; color: #fff; font-size: 9px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; vertical-align: -4px; }
.cal-kv__wide { grid-column: 1 / -1; }
.cal-kv__wide dd { font-weight: 400; white-space: pre-line; }
.cal-sheet__acts { display: grid; grid-template-columns: repeat(auto-fit, minmax(90px, 1fr)); gap: 8px; }
.cal-act {
  min-height: 46px; display: inline-flex; align-items: center; justify-content: center; gap: 6px; border-radius: 7px;
  border: 1.5px solid var(--p-line); background: #fff; color: var(--p-ink); font: inherit; font-size: 14.5px; font-weight: 700; text-decoration: none;
}
.cal-act--go { background: var(--p-green); border-color: var(--p-green); color: #fff; }
.cal-sheet__more { display: flex; justify-content: space-between; gap: 12px; border-top: 1px solid var(--p-line-soft); padding-top: 12px; }
.cal-sheet__more button, .cal-sheet__more a { border: 0; background: none; padding: 6px 0; font: inherit; font-size: 14.5px; font-weight: 700; color: var(--p-green-dark); text-decoration: none; }
.cal-sheet :focus-visible { outline: 3px solid var(--p-flame); outline-offset: 2px; }
</style>
